import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiDownload, FiX } from 'react-icons/fi';
import { LUXISTT_SHOW_PDF_EVENT } from '../api/client';
import './PdfViewerHost.css';

/**
 * Listens for {@link LUXISTT_SHOW_PDF_EVENT} and shows the PDF in a full-screen iframe (no new window).
 * Mount once inside the app shell (e.g. App.js).
 */
export default function PdfViewerHost() {
  const [doc, setDoc] = useState(null);

  const revokeAndClear = useCallback(() => {
    setDoc((prev) => {
      if (prev?.objectUrl) {
        URL.revokeObjectURL(prev.objectUrl);
      }
      return null;
    });
  }, []);

  useEffect(() => {
    const onShow = (e) => {
      const { blob, filename } = e.detail || {};
      if (!blob || !(blob instanceof Blob)) return;
      setDoc((prev) => {
        if (prev?.objectUrl) {
          URL.revokeObjectURL(prev.objectUrl);
        }
        const objectUrl = URL.createObjectURL(blob);
        return {
          objectUrl,
          filename: typeof filename === 'string' && filename.trim() ? filename.trim() : 'luxistt-document.pdf',
        };
      });
    };
    window.addEventListener(LUXISTT_SHOW_PDF_EVENT, onShow);
    return () => window.removeEventListener(LUXISTT_SHOW_PDF_EVENT, onShow);
  }, []);

  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') revokeAndClear();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [doc, revokeAndClear]);

  const onDownload = useCallback(() => {
    if (!doc) return;
    const a = document.createElement('a');
    a.href = doc.objectUrl;
    a.download = doc.filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [doc]);

  if (!doc) return null;

  return createPortal(
    <div className="lux-pdf-viewer-overlay" role="dialog" aria-modal="true" aria-label="PDF document">
      <div className="lux-pdf-viewer-backdrop" onClick={revokeAndClear} aria-hidden />
      <div className="lux-pdf-viewer-panel">
        <header className="lux-pdf-viewer-toolbar">
          <p className="lux-pdf-viewer-title" title={doc.filename}>
            {doc.filename}
          </p>
          <div className="lux-pdf-viewer-actions">
            <button type="button" className="lux-pdf-viewer-btn lux-pdf-viewer-btn--primary" onClick={onDownload}>
              <FiDownload size={16} aria-hidden />
              Download
            </button>
            <button
              type="button"
              className="lux-pdf-viewer-btn lux-pdf-viewer-btn--ghost"
              onClick={revokeAndClear}
              aria-label="Close PDF viewer"
            >
              <FiX size={18} aria-hidden />
              Close
            </button>
          </div>
        </header>
        <iframe className="lux-pdf-viewer-frame" title={doc.filename} src={doc.objectUrl} />
      </div>
    </div>,
    document.body,
  );
}
